// Login.tsx

import React, { useState, useEffect, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/AuthProvider";
import { getAuth } from "firebase/auth";

const ALLOW_SIGNUP = false;
const PLATFORM_NAME = "DinePlatform"
const GOOGLE_SIGNIN = true;

interface LoginFormProps {
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  setView: (view: string) => void;
}

interface SignUpFormProps {
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onPasswordCheckChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onTermsChange: (value: boolean) => void;
  onMarketingChange: (value: boolean) => void; // New prop
  agreeToTerms: boolean;
  agreeToMarketing: boolean; // New prop
  onSubmit: (e: React.FormEvent) => void;
}

// Styles object - can be moved to CSS file
const styles = {
  pageContainer: {
    height: "100vh",
    maxHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #f6f8fb 0%, #f5f5f5 100%)",
  },
  
  authContainer: {
    width: "100%",
    maxWidth: "420px",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
  },

  logoContainer: {
    width: "100%",
    height: "100px",
    paddingTop: "30px",
    display: "flex",
    justifyContent: "center",
    backgroundColor: "#000000",
  },

  authContent: {
    padding: "32px",
  },

  title: {
    fontSize: "24px",
    fontWeight: "600",
    marginBottom: "24px",
    textAlign: "center" as const,
    color: "#2d3748",
  },

  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },

  input: {
    width: "100%",
    padding: "12px 16px",
    boxSizing: "border-box" as "border-box",
    fontSize: "16px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    transition: "all 0.2s ease",
    outline: "none",
  },

  button: {
    width: "100%",
    padding: "12px 24px",
    marginTop: "0px",
    fontSize: "16px",
    fontWeight: "500",
    color: "#ffffff",
    // backgroundColor: "#4F46E5",
    backgroundColor: "black",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  googleButton: {
    width: "100%",
    padding: "12px 24px",
    marginTop: "8px",
    fontSize: "16px",
    fontWeight: "500",
    color: "#333333",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  secondaryButton: {
    width: "100%",
    padding: "12px 24px",
    boxSizing: "border-box" as "border-box",
    fontSize: "16px",
    fontWeight: "500",
    // color: "#4F46E5",
    color: "black",
    backgroundColor: "white",
    // border: "1px solid #4F46E5",
    border: "1px solid black",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    textAlign: "center" as "center"
  },

  toggleContainer: {
    display: "flex",
    borderRadius: "8px",
    overflow: "hidden",
    marginBottom: "24px",
    border: "1px solid #e2e8f0",
  },

  toggleButton: {
    flex: 1,
    padding: "12px",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontSize: "14px",
    fontWeight: "500",
  },

  selectedToggle: {
    // backgroundColor: "#4F46E5",
    backgroundColor: "black",
    color: "#ffffff",
  },

  linkText: {
    // color: "#4F46E5",
    color: "black",
    fontSize: "14px",
    cursor: "pointer",
    textAlign: "left" as const,
    marginTop: "8px",
    textDecoration: "none",
  },

  errorText: {
    color: "#e53e3e",
    fontSize: "14px",
    marginTop: "8px",
    textAlign: "center" as const,
  },

  checkbox: {
    fontSize: "12px",
    marginRight: "8px",
  },

  checkboxLabel: {
    fontSize: "12px",
    color: "#4a5568",
  },

  link: {
    // color: "#4F46E5",
    color: "black",
    textDecoration: "underline",
    marginLeft: "4px",
    marginRight: "4px",
  },

  divider: {
    display: "flex",
    alignItems: "center",
    marginTop: "16px",
    marginBottom: "16px",
  },

  dividerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: "#e2e8f0",
  },

  dividerText: {
    margin: "0 16px",
    color: "#718096",
    fontSize: "14px",
  },
  
  cooldownText: {
    marginTop: "8px",
    color: "#4a5568",
    fontSize: "14px",
    textAlign: "center" as const,
  },
  
  cooldownTimer: {
    fontWeight: "600",
    // color: "#4F46E5",
    color: "black"
  }
};

const LoginForm = memo(({ onEmailChange, onPasswordChange, onSubmit, setView }: LoginFormProps) => (
  <form style={styles.form} onSubmit={onSubmit}>
    <h1 style={styles.title}>{PLATFORM_NAME} Login</h1>
    <input
      style={styles.input}
      type="email"
      placeholder="Email Address"
      required
      onChange={(e) => onEmailChange(e.target.value)}
    />
    <input
      style={styles.input}
      type="password"
      placeholder="Password"
      required
      onChange={(e) => onPasswordChange(e.target.value)}
    />
    <div style={styles.linkText} onClick={() => setView("forgotPassword")}>
      Forgot Password?
    </div>
    <button type="submit" style={styles.button}>
      Login
    </button>
  </form>
));

const SignUpForm = memo(({ 
  onEmailChange, 
  onPasswordChange, 
  onPasswordCheckChange, 
  onNameChange, 
  onTermsChange, 
  onMarketingChange, 
  agreeToTerms, 
  agreeToMarketing, 
  onSubmit 
}: SignUpFormProps) => (
  <form style={styles.form} onSubmit={onSubmit}>
    <h1 style={styles.title}>Create Account</h1>
    <input
      style={styles.input}
      type="text"
      placeholder="Full Name"
      required
      onChange={(e) => onNameChange(e.target.value)}
    />
    <input
      style={styles.input}
      type="email"
      placeholder="Email Address"
      required
      onChange={(e) => onEmailChange(e.target.value)}
    />
    <input
      style={styles.input}
      type="password"
      placeholder="Password"
      required
      minLength={8}
      onChange={(e) => onPasswordChange(e.target.value)}
    />
    <input
      style={styles.input}
      type="password"
      placeholder="Confirm Password"
      required
      minLength={8}
      onChange={(e) => onPasswordCheckChange(e.target.value)}
    />
    <div style={{ marginTop: "16px" }}>
      <input
        type="checkbox"
        id="agreeToTerms"
        checked={agreeToTerms}
        onChange={(e) => onTermsChange(e.target.checked)}
        style={styles.checkbox}
        required
      />
      <label htmlFor="agreeToTerms" style={styles.checkboxLabel}>
        I agree to the
        <a href="/terms" target="_blank" rel="noopener noreferrer" style={styles.link}>
          Terms of Service
        </a>
        and
        <a href="/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>
          Privacy Policy
        </a>
      </label>
    </div>
    <div style={{}}>
      <input
        type="checkbox"
        id="agreeToMarketing"
        checked={agreeToMarketing}
        onChange={(e) => onMarketingChange(e.target.checked)}
        style={styles.checkbox}
      />
      <label htmlFor="agreeToMarketing" style={styles.checkboxLabel}>
        I would like to receive marketing communications about special offers and updates
      </label>
    </div>
    <button type="submit" style={styles.button}>
      Sign Up
    </button>
  </form>
));

const EmailVerificationForm = memo(({ onSendVerification }: { onSendVerification: (e: React.FormEvent) => void }) => {
  const { currentUser, verificationEmailSent, verificationCooldown } = useAuth();
  
  return (
    <form style={styles.form} onSubmit={onSendVerification}>
      <h1 style={styles.title}>Verify Your Email</h1>
      <p style={{ textAlign: "center", marginBottom: "16px" }}>
        {verificationEmailSent ? (
          <>
            A verification email has been sent to <strong>{currentUser?.email}</strong>.
            <br />
            Please check your inbox and click the verification link.
            {verificationCooldown > 0 && (
              <div style={styles.cooldownText}>
                You can request another email in <span style={styles.cooldownTimer}>{verificationCooldown} seconds</span>
              </div>
            )}
          </>
        ) : (
          <>
            Click the button below to send a verification email to <strong>{currentUser?.email}</strong>
          </>
        )}
      </p>
      <button 
        type="submit" 
        style={{
          ...styles.button,
          opacity: verificationCooldown > 0 ? 0.7 : 1,
          cursor: verificationCooldown > 0 ? 'not-allowed' : 'pointer'
        }}
        disabled={verificationCooldown > 0}
      >
        {verificationEmailSent 
          ? `Resend Verification Email${verificationCooldown > 0 ? ` (${verificationCooldown}s)` : ''}`
          : 'Send Verification Email'}
      </button>
    </form>
  );
});

const EmailVerificationMessage = memo(({ 
  onLogout, 
  onResendVerification, 
  updateMarketingPreference 
}: { 
  onLogout: () => void; 
  onResendVerification: (e: React.FormEvent) => void;
  updateMarketingPreference: (value: boolean, userEmail?: string) => Promise<void>;
}) => {  
  
  const { verificationCooldown } = useAuth();
  const navigate = useNavigate();

  // Function to process marketing preferences when user confirms verification
const processMarketingPreference = (userEmail: string) => {
  const marketingSignUpsStr = localStorage.getItem('marketingSignUps');
  if (!marketingSignUpsStr) return;
  
  try {
    const marketingSignUps = JSON.parse(marketingSignUpsStr);
    const userPrefs = marketingSignUps[userEmail];
    
    if (userPrefs && userPrefs.marketing && !userPrefs.fulfilled) {
      // Call the cloud function with the email parameter
      updateMarketingPreference(true, userEmail)
        .then(() => {
          // Update localStorage
          marketingSignUps[userEmail].fulfilled = true;
          localStorage.setItem('marketingSignUps', JSON.stringify(marketingSignUps));
          console.log(`Marketing preference processed for ${userEmail}`);
        })
        .catch(error => {
          console.error('Error updating marketing preference:', error);
        });
    }
  } catch (error) {
    console.error('Error processing marketing preferences from localStorage:', error);
  }
};
  
  // Function to check if email is verified
  const checkVerification = async () => {
    const auth = getAuth();
    if (auth.currentUser) {
      try {
        // Force refresh the user
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          // If email is verified, process any pending marketing preferences
          if (auth.currentUser.email) {
            processMarketingPreference(auth.currentUser.email);
          }
          // Redirect to home page if verified
          navigate("/");
        } else {
          alert("Your email is not verified yet. Please check your inbox and click the verification link.");
        }
      } catch (error) {
        console.error("Error checking verification:", error);
      }
    }
  };
  
  return (
    <form style={styles.form}>
      <h1 style={styles.title}>Email Verification</h1>
      <span style={{fontSize: "small", marginBottom: "5%"}}>
        A verification link has been sent to your email. Please check your inbox and click it to activate your account.
      </span>
      
      <button 
        type="button" 
        style={styles.button} 
        onClick={checkVerification}
      >
        I've Verified My Email
      </button>
      
      <button 
        type="button" 
        style={{
          ...styles.secondaryButton,
          opacity: verificationCooldown > 0 ? 0.7 : 1,
          cursor: verificationCooldown > 0 ? 'not-allowed' : 'pointer'
        }}
        disabled={verificationCooldown > 0}
        onClick={onResendVerification}
      >
        {verificationCooldown > 0 
          ? `Resend Verification Email (${verificationCooldown}s)`
          : 'Resend Verification Email'
        }
      </button>
      
      <div 
        style={{
          ...styles.secondaryButton, 
          border: "1px solid #e53e3e", 
          color: "#e53e3e"
        }} 
        onClick={onLogout}
      >
        Log Out and Return
      </div>
    </form>
  );
});

const ForgotPasswordForm = memo(({ onEmailChange, onSubmit }: { onEmailChange: (value: string) => void; onSubmit: (e: React.FormEvent) => void }) => (
  <form style={styles.form} onSubmit={onSubmit}>
    <h1 style={styles.title}>Reset Password</h1>
    <p style={{ textAlign: "center", marginBottom: "16px" }}>
      Enter your email address to receive a password reset link
    </p>
    <input
      style={styles.input}
      type="email"
      placeholder="Email Address"
      required
      onChange={(e) => onEmailChange(e.target.value)}
    />
    <button type="submit" style={styles.button}>
      Send Reset Link
    </button>
  </form>
));

const PasswordResetMessage = memo(() => (
  <div style={styles.form}>
    <h1 style={styles.title}>Password Reset Email Sent</h1>
    <p style={{ textAlign: "center", marginBottom: "16px" }}>
      Check your email for a link to reset your password. If it doesn't appear within a few minutes, check your spam folder.
    </p>
    <button type="button" style={styles.button} onClick={() => window.location.href = "/login"}>
      Return to Login
    </button>
  </div>
));

const AuthToggle = memo(({ view, setView }: { view: string; setView: (view: string) => void }) => (
  <div style={styles.toggleContainer}>
    <button
      style={{
        ...styles.toggleButton,
        ...(view === "login" ? styles.selectedToggle : {})
      }}
      onClick={() => setView("login")}
    >
      Login
    </button>
    <button
      style={{
        ...styles.toggleButton,
        ...(view === "signUp" ? styles.selectedToggle : {})
      }}
      onClick={() => setView("signUp")}
    >
      Sign Up
    </button>
  </div>
));

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get("redirect") || "/";
  const showRegister = searchParams.get("register") === "true";
  
  const {
    currentUser,
    loading,
    error,
    loginWithEmail,
    loginWithGoogle,
    signup,
    logout,
    sendVerificationEmail,
    resetPassword,
    agreeToMarketing,
    updateMarketingPreference
  } = useAuth();

  const [view, setView] = useState<string>(showRegister ? "signUp" : "login");
  const [errors, setErrors] = useState<string[]>([]);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordCheck, setPasswordCheck] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
  const [marketingConsent, setMarketingConsent] = useState<boolean>(false);

  // Clear any main app styles when the login page loads
  useEffect(() => {
    // Remove navigation classes
    document.body.classList.remove('nav-open', 'nav-closed');
    
    // Reset body styles
    document.body.style.background = '';
    document.body.style.backgroundColor = '#101014';
    document.body.style.backgroundImage = 'none';
    document.body.style.padding = '0';
    document.body.style.margin = '0';
    
    // Reset any grid applied by the main app
    document.body.style.backgroundImage = 'none';
    
    // Remove any daily planner view
    const appElement = document.querySelector('.app');
    if (appElement) {
      appElement.classList.remove('daily-planner-view');
    }
    
    return () => {
      // No need for cleanup as the main app will reset its own styles
    };
  }, []);

  useEffect(() => {
    setErrors([]);
  }, [view]);

  // Check if we need to process marketing preferences for a verified user
  useEffect(() => {
    if (currentUser?.emailVerified) {
      processMarketingPreference();
    }
  }, [currentUser]);

  // Process any pending marketing preferences from localStorage
  const processMarketingPreference = () => {
    if (!currentUser?.email) return;
    
    const marketingSignUpsStr = localStorage.getItem('marketingSignUps');
    if (!marketingSignUpsStr) return;
    
    try {
      const marketingSignUps = JSON.parse(marketingSignUpsStr);
      const userEmail = currentUser.email;
      const userPrefs = marketingSignUps[userEmail];
      
      if (userPrefs && userPrefs.marketing && !userPrefs.fulfilled && agreeToMarketing === null) {
        // We need to call the cloud function - passing userEmail explicitly
        updateMarketingPreference(true, userEmail)
          .then(() => {
            // Update localStorage
            marketingSignUps[userEmail].fulfilled = true;
            localStorage.setItem('marketingSignUps', JSON.stringify(marketingSignUps));
            console.log(`Marketing preference processed for ${userEmail}`);
          })
          .catch((error: any) => {
            console.error('Error updating marketing preference:', error);
          });
      }
    } catch (error) {
      console.error('Error processing marketing preferences from localStorage:', error);
    }
  };

  // Modified to handle saved view state from localStorage
  useEffect(() => {
    if (currentUser) {
      if (!currentUser.emailVerified) {
        // Check if we have a saved view
        const savedView = localStorage.getItem('loginView');
        if (savedView && (savedView === 'emailVerificationForm' || savedView === 'emailVerificationMessage')) {
          setView(savedView);
        } else {
          setView("emailVerificationForm");
          localStorage.setItem('loginView', 'emailVerificationForm');
        }
      } else {
        // Process marketing preferences for verified users
        processMarketingPreference();
        
        // Clear any saved view when user is verified
        localStorage.removeItem('loginView');
        setView("loggedIn");
        window.location.href = redirectUrl;
      }
    }
  }, [currentUser, navigate, redirectUrl]);

  const sendEmailVerificationRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentUser && !currentUser.emailVerified) {
      try {
        const result = await sendVerificationEmail();
        if (result.success) {
          setView("emailVerificationMessage");
          // Store this view in localStorage so it persists on refresh
          localStorage.setItem('loginView', 'emailVerificationMessage');
        } else if (result.cooldownRemaining > 0) {
          // We don't need to do anything here as the UI will show the cooldown
          // from the verificationCooldown state in AuthProvider
        }
      } catch (error) {
        console.error("Error sending verification email:", error);
        setErrors(["Failed to send verification email. Please try again later."]);
      }
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await resetPassword(email);
    if (result === "SUCCESS") {
      setView("passwordResetMessage");
    } else {
      setErrors([result]);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await loginWithEmail(email, password);
    if (result === "NOT_VERIFIED") {
      setView("emailVerificationForm");
      // Store this view in localStorage so it persists on refresh
      localStorage.setItem('loginView', 'emailVerificationForm');
    } else if (result.includes("ERROR")) {
      setErrors([result]);
    }
  };

  const handleGoogleLogin = async () => {
    await loginWithGoogle(redirectUrl);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setErrors(["ERROR: Name is required"]);
      return;
    }
    if (password !== passwordCheck) {
      setErrors(["ERROR: Passwords do not match"]);
      return;
    }
    if (!agreeToTerms) {
      setErrors(["ERROR: You must agree to the Terms of Service and Privacy Policy"]);
      return;
    }

    // Save marketing preferences to localStorage
    if (marketingConsent && email) {
      // Get existing marketing signups or initialize new object
      const marketingSignUpsStr = localStorage.getItem('marketingSignUps');
      const marketingSignUps = marketingSignUpsStr ? JSON.parse(marketingSignUpsStr) : {};
      
      // Add/update this user's preferences
      marketingSignUps[email] = {
        marketing: true,
        fulfilled: false
      };
      
      // Save back to localStorage
      localStorage.setItem('marketingSignUps', JSON.stringify(marketingSignUps));
    }

    const result = await signup(email, password, name);
    if (result.includes("ERROR")) {
      setErrors([result]);
    } else {
      setView("emailVerificationForm");
      localStorage.setItem('loginView', 'emailVerificationForm');
    }
  };

  const handleLogout = () => {
    // Clear verification-related localStorage items during logout
    localStorage.removeItem('verificationCooldown');
    localStorage.removeItem('verificationCooldownExpires');
    localStorage.removeItem('verificationEmailSent');
    localStorage.removeItem('loginView');
    
    logout("/login");
  };

  // Display error messages
  const ErrorMessages = () => (
    <>
      {errors.map((error, index) => (
        <p key={index} style={styles.errorText}>
          {error}
        </p>
      ))}
      {error && <p style={styles.errorText}>{error}</p>}
    </>
  );

  // Add a Social Login option
  const SocialLogin = () => (
    <>
      <div style={styles.divider}>
        <div style={styles.dividerLine}></div>
        <div style={styles.dividerText}>OR</div>
        <div style={styles.dividerLine}></div>
      </div>
      <button 
        type="button" 
        style={styles.googleButton}
        onClick={handleGoogleLogin}
      >
        <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
          <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
          <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
          <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
          <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
        </svg>
        Continue with Google
      </button>
    </>
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{display: "flex", width: "100vw", height: "100vh", alignItems: "center", justifyContent: "center", backgroundColor: "#101014"}}>
      <div style={styles.authContainer}>
        <div style={styles.authContent}>
          {!currentUser && view !== "forgotPassword" && view !== "passwordResetMessage" && (
            ALLOW_SIGNUP && <AuthToggle view={view} setView={setView} />
          )}
          
          {view === "login" && (
            <>
              <LoginForm
                onEmailChange={setEmail}
                onPasswordChange={setPassword}
                onSubmit={handleLogin}
                setView={setView}
              />
              {GOOGLE_SIGNIN && <SocialLogin />}
            </>
          )}
          
          {view === "signUp" && (
            <SignUpForm
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onPasswordCheckChange={setPasswordCheck}
              onNameChange={setName}
              onTermsChange={setAgreeToTerms}
              onMarketingChange={setMarketingConsent}
              agreeToTerms={agreeToTerms}
              agreeToMarketing={marketingConsent}
              onSubmit={handleSignUp}
            />
          )}
          
          {view === "forgotPassword" && (
            <ForgotPasswordForm
              onEmailChange={setEmail}
              onSubmit={handlePasswordReset}
            />
          )}
          
          {view === "passwordResetMessage" && (
            <PasswordResetMessage />
          )}
          
          {view === "emailVerificationForm" && (
            <>
              <EmailVerificationForm onSendVerification={sendEmailVerificationRequest} />
              <div style={styles.secondaryButton} onClick={handleLogout}>
                Log Out and Return
              </div>
            </>
          )}
          
          {view === "emailVerificationMessage" && (
            <EmailVerificationMessage 
              onLogout={handleLogout}
              onResendVerification={sendEmailVerificationRequest}
              updateMarketingPreference={updateMarketingPreference}
            />
          )}
          
          {view !== "login" && view !== "signUp" && view !== "forgotPassword" && 
           view !== "passwordResetMessage" && view !== "emailVerificationForm" && 
           view !== "emailVerificationMessage" && (
            <h1 style={styles.title}>Redirecting...</h1>
          )}
          
          <ErrorMessages />
        </div>
      </div>
    </div>
  );
};

export default Login;